import React, { useState, useEffect, useCallback } from 'react';
import { Flame, Trophy, Crown, Zap, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { creatorAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';

export default function TipRace({ roomId, isCreator, onTipSent }) {
  const [race, setRace] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [starting, setStarting] = useState(false);
  const [newTarget, setNewTarget] = useState('');
  const [newPrize, setNewPrize] = useState('');
  const [tipAmt, setTipAmt] = useState('');
  const [sending, setSending] = useState(false);

  const fetchRace = useCallback(async () => {
    try {
      const r = await creatorAPI.getActiveRace(roomId);
      setRace(r.data.race);
      setLeaderboard(r.data.leaderboard || []);
    } catch {}
  }, [roomId]);

  useEffect(() => {
    fetchRace();
    const interval = setInterval(fetchRace, 5000);
    return () => clearInterval(interval);
  }, [fetchRace]);

  const startRace = async () => {
    const target = parseFloat(newTarget);
    if (isNaN(target) || target <= 0 || !newPrize.trim()) return;
    try {
      const r = await creatorAPI.startRace(roomId, target, newPrize);
      setRace(r.data);
      setStarting(false);
      toast.success('Tip Race started!');
    } catch (e) { toast.error('Failed to start race'); }
  };

  const sendRaceTip = async () => {
    const amt = parseFloat(tipAmt);
    if (isNaN(amt) || amt <= 0) return;
    setSending(true);
    try {
      const r = await creatorAPI.raceTip(roomId, amt);
      toast.success(r.data.message);
      if (r.data.winner) {
        toast.success(`${r.data.winner.name} WINS the race!`, { duration: 5000 });
      }
      setTipAmt('');
      onTipSent?.(amt);
      fetchRace();
    } catch (e) { toast.error(e.response?.data?.detail || 'Tip failed'); }
    finally { setSending(false); }
  };

  const QUICK_AMOUNTS = [1, 5, 10, 25];
  const medals = ['text-yellow-400', 'text-zinc-300', 'text-amber-600'];

  return (
    <div className="border-t border-zinc-800 bg-zinc-900/50" data-testid="tip-race">
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-xs font-bold text-orange-400 flex items-center gap-1.5">
          <Flame size={12} /> Tip Race
        </span>
        {isCreator && !race && (
          <Button onClick={() => setStarting(!starting)} variant="ghost" size="sm" className="text-zinc-400 text-xs h-6 px-2" data-testid="start-race-btn">
            {starting ? 'Cancel' : 'Start Race'}
          </Button>
        )}
      </div>

      {/* Creator: start new race */}
      <AnimatePresence>
        {starting && !race && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-3 pb-3 space-y-1.5">
              <Input value={newTarget} onChange={e => setNewTarget(e.target.value)} type="number" placeholder="Target amount ($)" className="h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="race-target-input" />
              <Input value={newPrize} onChange={e => setNewPrize(e.target.value)} placeholder="Prize (e.g. 'Private video call')" className="h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="race-prize-input" />
              <Button onClick={startRace} size="sm" className="w-full h-7 bg-orange-500 text-white text-xs font-bold rounded" data-testid="confirm-start-race-btn">
                <Zap size={10} className="mr-1" /> Launch Race
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active race */}
      {race && race.status === 'active' && (
        <div className="px-3 pb-3">
          {/* Prize banner */}
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 mb-2">
            <Trophy size={14} className="text-amber-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-zinc-400">First to {formatCurrency(race.target_amount)} wins:</p>
              <p className="text-xs text-amber-300 font-bold truncate">{race.prize_description}</p>
            </div>
          </div>

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <div className="space-y-1 mb-2">
              {leaderboard.slice(0, 5).map(([userId, total], idx) => {
                const pct = Math.min(100, (total / race.target_amount) * 100);
                return (
                  <motion.div
                    key={userId}
                    initial={{ x: -10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-2"
                  >
                    <span className={`text-xs font-bold w-4 text-center ${medals[idx] || 'text-zinc-500'}`}>
                      {idx === 0 ? <Crown size={12} /> : `#${idx + 1}`}
                    </span>
                    <div className="flex-1">
                      <div className="w-full bg-zinc-800 rounded-full h-2 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          className={`h-full rounded-full ${idx === 0 ? 'bg-gradient-to-r from-orange-500 to-amber-400' : idx === 1 ? 'bg-zinc-500' : 'bg-zinc-600'}`}
                        />
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-zinc-400 w-12 text-right">{formatCurrency(total)}</span>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Quick tip buttons for viewers */}
          {!isCreator && (
            <div className="flex items-center gap-1.5">
              {QUICK_AMOUNTS.map(a => (
                <Button key={a} onClick={() => setTipAmt(a.toString())} variant="outline" size="sm"
                  className={`flex-1 h-7 text-xs rounded-full border-zinc-700 ${tipAmt === a.toString() ? 'bg-orange-500/20 border-orange-400 text-orange-400' : 'text-zinc-400'}`}>
                  ${a}
                </Button>
              ))}
              <Input type="number" value={tipAmt} onChange={e => setTipAmt(e.target.value)} placeholder="$" className="w-12 h-7 bg-zinc-800 border-zinc-700 text-white text-xs text-center" data-testid="race-tip-input" />
              <Button onClick={sendRaceTip} disabled={!tipAmt || sending} size="sm" className="h-7 px-3 bg-orange-500 text-white text-xs font-bold rounded-full" data-testid="send-race-tip-btn">
                <ChevronUp size={12} />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Completed race */}
      {race && race.status === 'completed' && (
        <div className="px-3 pb-3">
          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/30">
            <Trophy size={16} className="text-yellow-400" />
            <div>
              <p className="text-xs text-yellow-400 font-bold">Race Complete!</p>
              <p className="text-[10px] text-zinc-400">Prize: {race.prize_description}</p>
            </div>
          </div>
        </div>
      )}

      {/* No race, viewer view */}
      {!race && !isCreator && (
        <div className="px-3 pb-2">
          <p className="text-[10px] text-zinc-600 italic">No active race right now</p>
        </div>
      )}
    </div>
  );
}
